'use client'

import React, { useEffect } from 'react'
import Price from 'ui/components/Price'
import Message from 'ui/components/Message'
import CheckGreenIcon from 'ui/icons/CheckGreenIcon'
import Button from 'ui/components/Button'
import ProductInfo from 'ui/components/ProductInfo'
import Swiper from 'ui/components/Swiper'
import { useProductActions } from 'web/src/lib/context/product-context'
import Card from 'ui/components/Card'
import Image from 'next/image'
import routes from 'web/src/lib/routes'
import messages from 'ui/data/messages'
import { CartSidePanel } from 'web/src/lib/context/cart-show-context'
import StripHtml from 'ui/utils/stripHtml'
import { useCart } from 'medusa-react'
import { useStore } from 'web/src/lib/context/store-context'
import LoadingIcon from '../../icons/LoadingIcon'
import { trackViewItem } from 'web/src/utils/tracking/trackViewItem'
import { PricedProduct } from '@medusajs/medusa/dist/types/pricing'
import getVariantPrice from 'web/src/lib/util/getVariantPrice'

const ProductDetail = ({ product, relatedProducts }: { product: PricedProduct; relatedProducts: PricedProduct[] }) => {
    const { inStock, quantity, setQuantity, addToCart } = useProductActions()
    const { open } = CartSidePanel()
    const { quantityUpdating, lineItemError, quantityError } = useStore()
    const productMetadata = product?.metadata
    const carModelCompatibility = productMetadata?.car_model_compatibility as string[]
    const { variant } = useProductActions()
    const { cart } = useCart()
    const productInCart = cart?.items?.find(item => item.variant.product_id === variant?.product_id)
    const quantityInCart = productInCart ? productInCart?.quantity : 0
    const stock = variant?.inventory_quantity || 0
    const canAdd = variant?.allow_backorder || (quantity > 0 && quantityInCart + quantity <= stock && inStock)
    const cartLineItem = cart?.items?.find(item => item.variant_id == variant?.id)
    const isLoading = !!(quantityUpdating?.[`${variant?.id}`] || quantityUpdating?.[`${cartLineItem?.id}`])
    const handleAddToCart = () => {
        addToCart(() => open())
    }
    useEffect(() => {
        if (variant) {
            trackViewItem(variant)
        }
    }, [])

    const price = getVariantPrice(product.variants[0]?.prices)

    return (
        <>
            <div className="container py-8 md:py-16">
                <div className="flex gap-x-8">
                    <div className="hidden flex-wrap gap-6 lg:flex lg:w-1/2 lg:max-w-[50%]">
                        {product.thumbnail && (
                            <div className="w-full relative overflow-hidden">
                                {price?.calculatedPriceType === 'sale' && (
                                    <span className="absolute bg-brand-red text-white uppercase text-xl font-semibold tracking-wider px-14 -right-10 top-[26px] rotate-45">
                                        Sale
                                    </span>
                                )}
                                <Image
                                    priority
                                    loading="eager"
                                    src={product.thumbnail}
                                    alt={product.title || ''}
                                    width={1008}
                                    height={1008}
                                    className={'w-full'}
                                />
                            </div>
                        )}
                        {product.images?.map((productImage, index) => (
                            <div key={productImage.url} className="w-full lg:w-[calc(50%-12px)]">
                                <Image
                                    priority={index === 1 && !product.thumbnail}
                                    loading={index === 1 && !product.thumbnail ? 'eager' : 'lazy'}
                                    src={productImage.url}
                                    alt={product.title || ''}
                                    width={1008}
                                    height={1008}
                                    className={'w-full'}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="w-full 2xl:max-w-2xl lg:w-1/2 lg:max-w-[50%] 2xl:pl-20" id="product">
                        <div className="lg:sticky top-10 flex w-full flex-col gap-y-4">
                            <h1>{product.title}</h1>
                            <span>
                                {price?.calculatedPriceType === 'sale' && (
                                    <>
                                        <Price className="line-through opacity-50" price={price.originalPrice} />{' '}
                                    </>
                                )}
                                {price?.calculatedPrice && <Price productDetail price={price.calculatedPrice} />}
                            </span>
                            <p>Artikelnummer: {variant?.sku} </p>
                            <div className={'lg:hidden'}>
                                <Swiper
                                    slides={[
                                        ...(product.thumbnail
                                            ? [
                                                  <div className="w-full">
                                                      <Image
                                                          priority
                                                          loading="eager"
                                                          src={product.thumbnail}
                                                          alt={product.title || ''}
                                                          width={342}
                                                          height={342}
                                                          className={'h-full w-full object-contain'}
                                                      />
                                                  </div>,
                                              ]
                                            : []),
                                        ...(product.images?.map((productImage, index) => (
                                            <div key={productImage.url} className="w-full">
                                                <Image
                                                    priority={index === 1 && !product.thumbnail}
                                                    loading={index === 1 && !product.thumbnail ? 'eager' : 'lazy'}
                                                    src={productImage.url}
                                                    alt={product.title || ''}
                                                    width={342}
                                                    height={342}
                                                    className={'h-full w-full object-contain'}
                                                />
                                            </div>
                                        )) || []),
                                    ]}
                                />
                            </div>
                            {product.description && (
                                <div
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: product.description }}
                                />
                            )}
                            {carModelCompatibility && (
                                <Message color={'green'}>
                                    <div className="flex flex-col gap-y-2">
                                        <div className="flex items-center gap-x-2">
                                            <CheckGreenIcon />
                                            <p className="text-sm font-medium">Geschikt voor:</p>
                                        </div>
                                        {carModelCompatibility.map((compatibility, index) => {
                                            return (
                                                <p className="break-words text-xs" key={index}>
                                                    {compatibility}
                                                </p>
                                            )
                                        })}
                                    </div>
                                </Message>
                            )}
                            {inStock && (
                                <Message color={'green'}>
                                    <div className="flex flex-col gap-y-2">
                                        <div className="flex items-center gap-x-2">
                                            <CheckGreenIcon />
                                            <p className="text-sm font-medium">{messages.inStock}</p>
                                        </div>
                                        <p className="text-xs">{messages.deliveryUsp}</p>
                                    </div>
                                </Message>
                            )}
                            <div className="flex">
                                <input
                                    name="quantity"
                                    id="quantity"
                                    type="number"
                                    min={1}
                                    defaultValue={1}
                                    className="mr-4 w-16 border border-black text-center font-medium"
                                    onChange={e => {
                                        setQuantity(Number(e.target.value))
                                    }}
                                />
                                <Button
                                    className="w-full"
                                    as="button"
                                    disabled={!canAdd}
                                    onClick={handleAddToCart}
                                    variant="primary"
                                >
                                    {isLoading && <LoadingIcon />}
                                    {inStock ? messages.addToCart : messages.notInStock}
                                </Button>
                            </div>
                            <div className="w-full border-t border-black" />
                            <div className="flex justify-between px-4">
                                <p className="text-sm font-medium">{messages.needHelp}</p>
                                <Button
                                    as={'a'}
                                    className="text-sm font-medium underline decoration-2 underline-offset-4"
                                    href={'mailto:webshop@mmsn.nl'}
                                >
                                    {messages.getInTouch}
                                </Button>
                            </div>
                            <ProductInfo
                                sku={product.variants.map(v => v.sku).join(' / ')}
                                metadata={productMetadata}
                            />
                        </div>
                    </div>
                </div>
                {relatedProducts.length > 0 && (
                    <div className="mt-16 flex flex-col gap-4 ">
                        <p className="text-2xl font-medium">Gerelateerde producten</p>
                        <div
                            className={
                                'flex flex-wrap gap-8 lg:flex md:[&>*]:w-[calc(50%_-_21.33333px)] lg:[&>*]:w-[calc(33.33333%_-_21.33333px)] [&>*]:max-w-[100%]'
                            }
                        >
                            {relatedProducts.map(product => {
                                return (
                                    <Card
                                        key={product.id}
                                        title={product.title || ''}
                                        prices={product.variants[0]?.prices}
                                        to={`${routes.product}/${product.handle}`}
                                        image={
                                            <Image
                                                className="w-full"
                                                src={product.thumbnail || ''}
                                                alt={`Product ${product.title || ''}`}
                                                width={288}
                                                height={240}
                                            />
                                        }
                                    />
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default ProductDetail
